import type { GameSnapshot, UnitState } from "./shared/types";

type MotionFrame = { time: number; units: Map<string, UnitState> };

// Start with one snapshot of headroom; add delay only for measured jitter,
// never for high ping alone. Recover gradually without rewinding positions.
export class SnapshotMotion {
  private frames: MotionFrame[] = [];
  private cursor = 0;
  private lastFrameAt = 0;
  private frozen = false;
  private delayMs = 100;
  private lastReceivedAt = 0;
  private jitterMs = 0;

  push(snapshot: GameSnapshot, receivedAt: number): void {
    const frame = { time: snapshot.serverTime, units: new Map(snapshot.units.map(unit => [unit.id, unit])) };
    const latest = this.frames[this.frames.length - 1];
    if (latest && frame.time < latest.time) return;
    const frozen = snapshot.isPaused || snapshot.phase === "finished";
    if (!latest || frozen || this.frozen !== frozen) {
      this.delayMs = 100;
      this.jitterMs = 0;
      this.frames = [frame];
      this.cursor = frame.time - (frozen ? 0 : this.delayMs);
      this.lastFrameAt = receivedAt;
    } else if (frame.time === latest.time) {
      this.frames[this.frames.length - 1] = frame;
    } else {
      const interval = frame.time - latest.time;
      const variation = Math.abs((receivedAt - this.lastReceivedAt) - interval);
      this.jitterMs = Math.max(variation, this.jitterMs * .97);
      this.delayMs = Math.max(100, Math.min(350, interval + this.jitterMs));
      this.frames.push(frame);
      // ponytail: keep at most 12 snapshots. After a long outage,
      // resynchronize instead of replaying an ever-growing movement backlog.
      if (this.frames.length > 12) this.frames.shift();
    }
    this.frozen = frozen;
    this.lastReceivedAt = receivedAt;
  }

  sample(now: number): { from: MotionFrame; to: MotionFrame; amount: number } | undefined {
    if (!this.frames.length) return undefined;
    const latest = this.frames[this.frames.length - 1].time;
    const elapsed = Math.max(0, now - this.lastFrameAt);
    this.lastFrameAt = now;
    if (!this.frozen) {
      const headroom = latest - this.cursor;
      // Gently correct clock drift/buffer starvation; never jump backwards
      // when a late packet arrives, or predict movement through obstacles.
      const speed = headroom > this.delayMs + 50 ? 1.1 : headroom < this.delayMs - 100 ? 0.9 : 1;
      this.cursor = Math.min(latest, elapsed > 1000 || this.cursor < this.frames[0].time - this.delayMs
        ? Math.max(this.cursor, latest - this.delayMs) : this.cursor + elapsed * speed);
    }
    while (this.frames.length > 2 && this.frames[1].time <= this.cursor) this.frames.shift();
    const from = this.frames[0], to = this.frames[1] ?? from;
    const amount = from === to ? 1 : Math.max(0, Math.min(1, (this.cursor - from.time) / (to.time - from.time)));
    return { from, to, amount };
  }
}

// Measure server progress over a full second of server time. Packet bursts
// must not masquerade as changes to the authoritative simulation rate.
export class ServerRate {
  private start?: Pick<GameSnapshot, "roomCode" | "tick" | "serverTime" | "isPaused">;
  private rate?: number;

  sample(snapshot: GameSnapshot): number | undefined {
    if (!this.start || this.start.roomCode !== snapshot.roomCode ||
        this.start.isPaused !== snapshot.isPaused || snapshot.tick < this.start.tick) {
      this.start = { roomCode: snapshot.roomCode, tick: snapshot.tick,
        serverTime: snapshot.serverTime, isPaused: snapshot.isPaused };
      this.rate = undefined;
    }
    const elapsed = snapshot.serverTime - this.start.serverTime;
    if (elapsed >= 1000) {
      this.rate = (snapshot.tick - this.start.tick) * 1000 / elapsed;
      this.start = { roomCode: snapshot.roomCode, tick: snapshot.tick,
        serverTime: snapshot.serverTime, isPaused: snapshot.isPaused };
    }
    return this.rate;
  }
}
